import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import API from "../services/API";
import { setSession } from "../services/auth";
import { landingPageToPath } from "../services/settings";
import { extractErrorMessage } from "../utils/errorMessage";

export default function MagicLinkCallback() {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const token = searchParams.get("token");

 const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
 const [error, setError] = useState<string | null>(null);

 useEffect(() => {
 if (!token) {
 setStatus("error");
 setError("No token provided in the URL.");
 return;
 }

 const verify = async () => {
 try {
 const res = await API.get(`/api/auth/magic-link/verify`, {
 params: { token },
 });

 const payload = res.data?.data;
 const accessToken: string | undefined = payload?.accessToken ?? payload?.token;
 const refreshToken: string | undefined = payload?.refreshToken;
 const role: string | undefined = payload?.user?.role;

 if (!accessToken || !role) {
 setStatus("error");
 setError("Verification succeeded but the response was malformed.");
 return;
 }

 setSession({ token: accessToken, refreshToken: refreshToken ?? null });

 if (payload?.mustChangePassword === true) {
 navigate("/change-password", { replace: true });
 return;
 }

 setStatus("success");
 setTimeout(() => {
 navigate(landingPageToPath(undefined, payload?.user?.id), { replace: true });
 }, 800);
 } catch (err: unknown) {
 console.error("Magic link verification error: ", err);
 const apiMessage = extractErrorMessage(err,"");
 setStatus("error");
 setError(apiMessage ||"Invalid or expired magic link.");
 }
 };

 verify();
 }, [token, navigate]);

 return (
 <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-slate-950">
 <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40"/>
 <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute -bottom-48 -left-32 h-[34rem] w-[34rem] rounded-full bg-emerald-300/30 blur-3xl"/>

 <div className="relative mx-auto w-full max-w-md px-4">
 <div className="rounded-3xl border border-white/60 bg-white/80 p-8 shadow-2xl backdrop-blur-xl">
 {status ==="loading"&& (
 <div className="text-center">
 <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-emerald-500"/>
 <h2 className="text-xl font-bold text-slate-900">Logging you in...</h2>
 <p className="mt-1 text-sm text-slate-500">Please wait while we verify your magic link.</p>
 </div>
 )}

 {status ==="success"&& (
 <div className="text-center">
 <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
 <svg className="h-6 w-6 text-role-primary"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="3">
 <path d="M20 6L9 17l-5-5"/>
 </svg>
 </div>
 <h2 className="text-xl font-bold text-slate-900">Success!</h2>
 <p className="mt-1 text-sm text-slate-500">You are being redirected to your dashboard.</p>
 </div>
 )}

 {status ==="error"&& (
 <div className="text-center">
 <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
 <svg className="h-6 w-6 text-rose-600"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2">
 <circle cx="12"cy="12"r="10"/>
 <path d="M15 9l-6 6M9 9l6 6"/>
 </svg>
 </div>
 <h2 className="text-xl font-bold text-slate-900">Login Failed</h2>
 <p className="mt-1 text-sm text-rose-600">{error}</p>
 <button
 type="button"onClick={() => navigate("/", { replace: true })}
 className="mt-5 inline-flex items-center justify-center rounded-xl bg-role-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110">
 Go to Login
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}
